import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './FlyerAnalysis.css';
import { getCountryName, sortCountries, ALL_COUNTRIES } from './countries';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

export default function FlyerAnalysisInsights() {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();

  const [countries, setCountries] = useState(ALL_COUNTRIES);
  const [country, setCountry] = useState(() => searchParams.get('country') || localStorage.getItem('fa_country') || '');
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState(null);
  const [seasonalData, setSeasonalData] = useState(null);
  const [retailerData, setRetailerData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [coOccurrenceData, setCoOccurrenceData] = useState(null);
  const [discountPlacementData, setDiscountPlacementData] = useState(null);
  const [pageSequencingData, setPageSequencingData] = useState(null);
  const [insufficientData, setInsufficientData] = useState(false);

  // Fetch countries
  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCountries(sortCountries(res.data.countries || []));
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    })();
  }, [getAccessTokenSilently]);

  const fetchInsights = useCallback(async () => {
    if (!country) return;
    setLoading(true);
    setInsufficientData(false);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const base = `${API_BASE}/api/flyer-analysis/insights`;

      // First check if we have enough data via stats
      const statsRes = await axios.get(`${API_BASE}/api/flyer-analysis/stats/${country}`, { headers });
      if ((statsRes.data.total_pages_analyzed || 0) < 20) {
        setInsufficientData(true);
        setLoading(false);
        return;
      }

      const [catRes, seaRes, retRes, brandRes, coRes, discRes, seqRes] = await Promise.all([
        axios.get(`${base}/category-placement?country=${country}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${base}/seasonal?country=${country}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${base}/retailer-profiles?country=${country}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${base}/brand-ads?country=${country}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${base}/category-cooccurrence?country=${country}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${base}/discount-placement?country=${country}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${base}/page-sequencing?country=${country}`, { headers }).catch(() => ({ data: null })),
      ]);

      setCategoryData(catRes.data);
      setSeasonalData(seaRes.data);
      setRetailerData(retRes.data);
      setBrandData(brandRes.data);
      setCoOccurrenceData(coRes.data);
      setDiscountPlacementData(discRes.data);
      setPageSequencingData(seqRes.data);
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, country]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleCountryChange = (val) => {
    setCountry(val);
    localStorage.setItem('fa_country', val);
    setSearchParams({ country: val });
  };

  // Helpers
  const getMode = (arr) => {
    if (!arr || arr.length === 0) return '—';
    const counts = {};
    arr.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  };

  const categoryPatterns = categoryData?.patterns || [];
  const seasonalPatterns = seasonalData?.patterns || [];
  const profiles = retailerData?.profiles || [];
  const brandPatterns = brandData?.patterns || [];
  const coOccurrencePairs = coOccurrenceData?.pairs || [];
  const discountCorrelations = discountPlacementData?.correlations || [];
  const pageSequences = pageSequencingData?.sequences || [];

  return (
    <div className="main-column" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="fa-header">
        <div className="fa-header-left">
          <div>
            <h1 className="fa-header-title">Insights</h1>
            <p className="fa-header-subtitle">Category placement, seasonal patterns, and more</p>
          </div>
        </div>
        <div className="fa-header-actions">
          <select className="fa-country-select" value={country} onChange={(e) => handleCountryChange(e.target.value)}>
            <option value="" disabled>Select Country</option>
            {countries.map(c => <option key={c} value={c}>{getCountryName(c)}</option>)}
          </select>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="fa-subnav">
        <a href="/flyer-analysis" className="fa-subnav-link">Dashboard</a>
        <a href="/flyer-analysis/new-job" className="fa-subnav-link">New Job</a>
        <a href={`/flyer-analysis/browse?country=${country}`} className="fa-subnav-link">Browse Flyers</a>
        <a href={`/flyer-analysis/insights?country=${country}`} className="fa-subnav-link active">Insights</a>
      </div>

      <div className="fa-scroll-content">
        {!country ? (
          <div className="fa-empty">
            <p>Select a country to view insights</p>
          </div>
        ) : loading ? (
          <div className="fa-loading">Loading insights...</div>
        ) : insufficientData ? (
          <div className="fa-info-banner">
            <p>Analyze more flyers to generate insights. At least 20 analyzed pages are needed for meaningful patterns.</p>
          </div>
        ) : (
          <div className="fa-insights-grid">
            {/* Category Placement */}
            <div className="fa-insight-card full-width">
              <h3 className="fa-insight-title">Category Placement</h3>
              {categoryPatterns.length === 0 ? (
                <div className="fa-empty"><p>No category data available</p></div>
              ) : (
                <table className="fa-placement-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Total</th>
                      <th>Front Cover</th>
                      <th>Inner</th>
                      <th>Back Cover</th>
                      <th>Avg Page #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryPatterns.map((cat) => {
                      const placements = cat.placements || [];
                      const total = cat.total_appearances || 0;
                      const getPlacement = (role) => placements.find(p => p.page_role === role);
                      const frontCover = getPlacement('front_cover');
                      const inner = getPlacement('inner');
                      const backCover = getPlacement('back_cover');
                      const frontPct = total > 0 && frontCover ? Math.round((frontCover.count / total) * 100) : 0;
                      const innerPct = total > 0 && inner ? Math.round((inner.count / total) * 100) : 0;
                      const backPct = total > 0 && backCover ? Math.round((backCover.count / total) * 100) : 0;
                      const avgPage = placements.length > 0
                        ? (placements.reduce((sum, p) => sum + (p.avg_page_number || 0) * p.count, 0) / total).toFixed(1)
                        : '—';

                      return (
                        <tr key={cat._id}>
                          <td style={{ fontWeight: 500 }}>{cat._id}</td>
                          <td>{total}</td>
                          <td>
                            <span className="fa-placement-bar" style={{ width: `${frontPct}%`, maxWidth: 80 }} />
                            {frontPct}%
                          </td>
                          <td>
                            <span className="fa-placement-bar" style={{ width: `${innerPct}%`, maxWidth: 80 }} />
                            {innerPct}%
                          </td>
                          <td>
                            <span className="fa-placement-bar" style={{ width: `${backPct}%`, maxWidth: 80 }} />
                            {backPct}%
                          </td>
                          <td style={{ color: '#6b7280' }}>{avgPage}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Seasonal Patterns */}
            <div className="fa-insight-card full-width">
              <h3 className="fa-insight-title">Seasonal Patterns</h3>
              {seasonalPatterns.length === 0 ? (
                <div className="fa-empty"><p>No seasonal data available</p></div>
              ) : (
                <div className="fa-seasonal-grid">
                  {['spring', 'summer', 'fall', 'winter'].map(season => {
                    const data = seasonalPatterns.find(s => s._id === season);
                    const categories = data?.categories || [];
                    return (
                      <div key={season} className="fa-season-col">
                        <h4 className="fa-season-title">{season}</h4>
                        {categories.length === 0 ? (
                          <div style={{ fontSize: 13, color: '#9ca3af' }}>No data</div>
                        ) : (
                          categories.slice(0, 8).map((cat, i) => (
                            <div key={i} className="fa-season-item">
                              <div className="fa-season-cat">{cat.category}</div>
                              <div className="fa-season-meta">
                                {cat.product_count} products · {cat.avg_discount?.toFixed(0) || 0}% avg discount · {cat.hero_count || 0} heroes
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Retailer Profiles */}
            <div className="fa-insight-card">
              <h3 className="fa-insight-title">Retailer Profiles</h3>
              {profiles.length === 0 ? (
                <div className="fa-empty"><p>No retailer data available</p></div>
              ) : (
                <div className="fa-profile-grid">
                  {profiles.slice(0, 9).map((p) => (
                    <div key={p._id} className="fa-profile-card">
                      <h4 className="fa-profile-name">{p.retailer_name || p._id}</h4>
                      <div className="fa-profile-stat-row">
                        <span className="fa-profile-stat-label">Pages</span>
                        <span className="fa-profile-stat-value">{p.total_pages_analyzed}</span>
                      </div>
                      <div className="fa-profile-stat-row">
                        <span className="fa-profile-stat-label">Flyers</span>
                        <span className="fa-profile-stat-value">{p.flyer_count}</span>
                      </div>
                      <div className="fa-profile-stat-row">
                        <span className="fa-profile-stat-label">Avg Products/Page</span>
                        <span className="fa-profile-stat-value">{p.avg_products_per_page?.toFixed(1)}</span>
                      </div>
                      <div className="fa-profile-stat-row">
                        <span className="fa-profile-stat-label">Layout</span>
                        <span className="fa-profile-stat-value">{getMode(p.layout_types)}</span>
                      </div>
                      <div className="fa-profile-stat-row">
                        <span className="fa-profile-stat-label">Style</span>
                        <span className="fa-profile-stat-value">{getMode(p.visual_styles)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Brand Advertising */}
            <div className="fa-insight-card">
              <h3 className="fa-insight-title">Brand Advertising</h3>
              {brandPatterns.length === 0 ? (
                <div className="fa-empty"><p>No brand advertising data</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="fa-placement-table">
                    <thead>
                      <tr>
                        <th>Brand</th>
                        <th>Placements</th>
                        <th>Retailers</th>
                        <th>Prominence</th>
                        <th>Ad Type</th>
                        <th>Page Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandPatterns.slice(0, 20).map((b, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>{b.brand}</td>
                          <td>{b.total_placements}</td>
                          <td>{b.retailer_count}</td>
                          <td>
                            <div className="fa-prominence">
                              {[1, 2, 3, 4, 5].map(n => (
                                <div key={n} className={`fa-prominence-dot ${n <= Math.round(b.avg_prominence || 0) ? 'filled' : ''}`} />
                              ))}
                            </div>
                          </td>
                          <td style={{ fontSize: 12 }}>{getMode(b.ad_types)}</td>
                          <td style={{ fontSize: 12 }}>{getMode(b.page_roles)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Category Co-occurrence */}
            <div className="fa-insight-card">
              <h3 className="fa-insight-title">Category Co-occurrence</h3>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Categories that frequently appear together on the same page</p>
              {coOccurrencePairs.length === 0 ? (
                <div className="fa-empty"><p>No co-occurrence data available</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="fa-placement-table">
                    <thead>
                      <tr>
                        <th>Category 1</th>
                        <th>Category 2</th>
                        <th>Pages Together</th>
                        <th>Strength</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coOccurrencePairs.slice(0, 15).map((pair, i) => {
                        const maxCount = coOccurrencePairs[0]?.co_occurrence_count || 1;
                        const strength = Math.round((pair.co_occurrence_count / maxCount) * 100);
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{pair.category_1}</td>
                            <td style={{ fontWeight: 500 }}>{pair.category_2}</td>
                            <td>{pair.co_occurrence_count}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 80, height: 6, background: '#f3f4f6', borderRadius: 3 }}>
                                  <div style={{ width: `${strength}%`, height: '100%', background: '#f97316', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 12, color: '#6b7280' }}>{strength}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Discount vs Placement */}
            <div className="fa-insight-card">
              <h3 className="fa-insight-title">Discount vs Placement</h3>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>How discount depth correlates with product cell size and page position</p>
              {discountCorrelations.length === 0 ? (
                <div className="fa-empty"><p>No discount correlation data available</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="fa-placement-table">
                    <thead>
                      <tr>
                        <th>Cell Size</th>
                        <th>Products</th>
                        <th>Avg Discount</th>
                        <th>Max Discount</th>
                        <th>Front Cover</th>
                        <th>Inner</th>
                        <th>Back Cover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discountCorrelations.map((d, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500, textTransform: 'capitalize' }}>{d.cell_size || '—'}</td>
                          <td>{d.count}</td>
                          <td style={{ color: '#16a34a', fontWeight: 500 }}>{d.avg_discount}%</td>
                          <td style={{ color: '#ff8264' }}>{d.max_discount}%</td>
                          <td>{d.placement_distribution?.front_cover || 0}</td>
                          <td>{d.placement_distribution?.inner || 0}</td>
                          <td>{d.placement_distribution?.back_cover || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Page Sequencing */}
            <div className="fa-insight-card full-width">
              <h3 className="fa-insight-title">Page Sequencing</h3>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>How categories flow through flyer pages — use this to determine page ordering</p>
              {pageSequences.length === 0 ? (
                <div className="fa-empty"><p>No page sequencing data available</p></div>
              ) : (
                <div className="fa-seasonal-grid" style={{ gridTemplateColumns: `repeat(${Math.min(pageSequences.length, 6)}, 1fr)` }}>
                  {pageSequences.map((seq) => (
                    <div key={seq.page_number} className="fa-season-col">
                      <h4 className="fa-season-title">Page {seq.page_number}</h4>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>{seq.total_products} products</div>
                      {seq.categories.map((cat, i) => (
                        <div key={i} className="fa-season-item">
                          <div className="fa-season-cat">{cat.category}</div>
                          <div className="fa-season-meta">
                            {cat.product_count} products{cat.avg_discount > 0 ? ` · ${cat.avg_discount}% discount` : ''}{cat.hero_count > 0 ? ` · ${cat.hero_count} heroes` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
